import {
  Action,
  Actions,
  Button,
  Card,
  FlagProps,
  Flags,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isEmpty} from 'ramda';

import {TelephonyLine, useDeleteTelephonyLineMutation} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {handleApiError, queryParams, usePermissions} from '@dms/shared';

import {buildArray, composePath, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {AttachTelephonyNumberForm} from '../../../components/AttachTelephonyNumberForm/AttachTelephonyNumberForm';
import {getTelephonyLineState, TelephonyLineState} from '../utils/getTelephonyLineState';
import {TelephonyLineNumberRow} from './TelephonyLineNumberRow';
import {TelephonyLineRoutingSummary} from './TelephonyLineRoutingSummary';

interface TelephonyLineCardProps extends RequiredTestIdProps {
  line: TelephonyLine;
}

const getStateFlagProps = (state: TelephonyLineState): FlagProps =>
  match(state)
    .with('ACTIVE', () => ({
      label: i18n.t('page.settings.telephony.stateActive'),
      colorScheme: 'green' as const,
    }))
    .with('IN_SETUP', () => ({
      label: i18n.t('page.settings.telephony.stateInSetup'),
      colorScheme: 'blue' as const,
    }))
    .with('NUMBER_MISSING', () => ({
      label: i18n.t('page.settings.telephony.stateNumberMissing'),
      colorScheme: 'orange' as const,
    }))
    .with('NUMBER_UNKNOWN', () => ({
      label: i18n.t('page.settings.telephony.stateUnknown'),
      colorScheme: 'neutral' as const,
      isSubtle: true,
    }))
    .with('AWAITING_FIRST_CALL', () => ({
      label: i18n.t('page.settings.telephony.stateAwaitingFirstCall'),
      colorScheme: 'yellow' as const,
    }))
    .exhaustive();

export function TelephonyLineCard(props: TelephonyLineCardProps) {
  const navigate = useNavigate();

  const [deleteLine] = useDeleteTelephonyLineMutation();

  const [canWriteTelephonySettings] = usePermissions({
    permissionKeys: ['telephonySettingsWrite'],
  });

  const lineDetailPath = composePath(settingsRoutes.telephonyLineDetail, {
    params: {lineId: props.line.id},
  });

  const handleEdit = () => navigate(lineDetailPath);

  const handleEditRouting = () =>
    navigate(`${lineDetailPath}?${queryParams.COMPONENT_SECTIONS_TAB}=routing`);

  const handleConnectNumber = () =>
    openDialog(
      <AttachTelephonyNumberForm
        lineId={props.line.id}
        data-testid={suffixTestId('attachNumber', props)}
      />,
      {
        id: 'attachTelephonyNumberDialog',
        title: i18n.t('page.settings.telephony.connectCardTitle'),
        size: 'small',
      }
    );

  const handleDelete = () =>
    openDeleteDialog({
      'data-testid': suffixTestId('deleteDialog', props),
      onConfirm: () =>
        deleteLine({lineId: props.line.id})
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
          .catch(handleApiError),
    });

  const actions = buildArray<Action>()
    .when(canWriteTelephonySettings, {
      type: 'iconButtonWithTooltip',
      icon: 'action/delete',
      tooltip: {label: i18n.t('general.actions.delete')},
      onClick: handleDelete,
      'data-testid': suffixTestId('delete', props),
    })
    .add({
      type: 'iconButtonWithTooltip',
      icon: 'image/edit',
      tooltip: {label: i18n.t('general.actions.edit')},
      onClick: handleEdit,
      'data-testid': suffixTestId('edit', props),
    });

  return (
    <Card variant="inlineGrey" data-testid={suffixTestId('card', props)}>
      <VStack spacing={4}>
        <HStack justify="space-between" align="center" spacing={2}>
          <HStack spacing={2} align="center" wrap>
            <Text size="base" alternative data-testid={suffixTestId('name', props)}>
              {props.line.name}
            </Text>
            <Flags
              size="small"
              flags={[getStateFlagProps(getTelephonyLineState(props.line))]}
              data-testid={suffixTestId('state', props)}
            />
          </HStack>
          <Actions actions={actions} size="small" data-testid={suffixTestId('actions', props)} />
        </HStack>
        {isEmpty(props.line.twilioNumbers) ? (
          <Card variant="inlineWhite" data-testid={suffixTestId('noNumber', props)}>
            <HStack justify="space-between" align="center" spacing={2}>
              <Text size="small" color="secondary">
                {i18n.t('page.settings.telephony.noNumberConnectedHint')}
              </Text>
              <Show when={canWriteTelephonySettings}>
                <Button
                  variant="secondary"
                  title={i18n.t('page.settings.telephony.connectNumber')}
                  onClick={handleConnectNumber}
                  data-testid={suffixTestId('connectNumber', props)}
                />
              </Show>
            </HStack>
          </Card>
        ) : (
          <VStack spacing={3}>
            {props.line.twilioNumbers.map((number) => (
              <TelephonyLineNumberRow
                key={number.sid}
                number={number}
                data-testid={suffixTestId(`number-[${number.sid}]`, props)}
              />
            ))}
          </VStack>
        )}
        <TelephonyLineRoutingSummary
          line={props.line}
          canEdit={canWriteTelephonySettings}
          onEdit={handleEditRouting}
          data-testid={suffixTestId('routing', props)}
        />
      </VStack>
    </Card>
  );
}
